/**
 * Utility functions for data transformations (transpose, pivot)
 */

/**
 * Transposes a data array, turning rows into columns and vice versa
 * @param {Array} data - Original data array of objects
 * @param {Object} schema - Schema object with fields property
 * @returns {Object} - Object containing the transposed data and new schema
 */
export const transposeData = (data, schema) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return { transposedData: [], transposedSchema: schema };
  }

  // Extract column names from first row
  const columnNames = Object.keys(data[0]);
  
  // Create the field column as first column in the transposed data
  const transposedData = [];
  
  // Add a row for each original column
  columnNames.forEach(columnName => {
    const newRow = { Field: columnName };
    
    // For each original row, create a new column in the transposed data
    data.forEach((row, rowIndex) => {
      // Use index + 1 to create column names like Value_1, Value_2, etc.
      const newColumnName = `Value_${rowIndex + 1}`;
      newRow[newColumnName] = row[columnName];
    });
    
    transposedData.push(newRow);
  });

  // Create the new schema for transposed data
  const transposedSchema = {
    fields: [
      { name: 'Field', type: 'STRING', mode: 'REQUIRED' },
      ...Array.from({ length: data.length }, (_, i) => ({
        name: `Value_${i + 1}`,
        // Try to determine the type from the original schema if possible
        type: 'STRING',
        mode: 'NULLABLE'
      }))
    ]
  };

  return { transposedData, transposedSchema };
};

/**
 * Creates a pivot table from the data
 * @param {Array} data - Original data array of objects
 * @param {Object} pivotConfig - Configuration for the pivot table
 * @param {string} pivotConfig.rowField - Field to use for rows
 * @param {string} pivotConfig.columnField - Field to use for columns
 * @param {string} pivotConfig.valueField - Field to use for values
 * @param {string} pivotConfig.aggregationType - Type of aggregation (sum, avg, count, etc.)
 * @param {Object} schema - Schema object with fields property
 * @returns {Object} - Object containing the pivoted data and new schema
 */
export const pivotData = (data, pivotConfig, schema) => {
  if (!data || !Array.isArray(data) || data.length === 0 || !pivotConfig) {
    return { pivotedData: [], pivotedSchema: schema };
  }

  const { rowField, columnField, valueField, aggregationType = 'sum' } = pivotConfig;
  
  // Get unique values for column field
  const uniqueColumnValues = [...new Set(data.map(row => 
    typeof row[columnField] === 'object' && row[columnField] !== null 
      ? (row[columnField].displayValue || row[columnField].value || 'N/A') 
      : (row[columnField] || 'N/A')
  ))].sort();
  
  // Get unique values for row field
  const uniqueRowValues = [...new Set(data.map(row => 
    typeof row[rowField] === 'object' && row[rowField] !== null 
      ? (row[rowField].displayValue || row[rowField].value || 'N/A') 
      : (row[rowField] || 'N/A')
  ))].sort();

  // Initialize a map to store aggregation results
  const pivotMap = {};
  
  // Initialize the map with row values
  uniqueRowValues.forEach(rowValue => {
    pivotMap[rowValue] = { [rowField]: rowValue };
    uniqueColumnValues.forEach(colValue => {
      pivotMap[rowValue][colValue] = 0; // Default value, will be updated based on aggregation
    });
  });

  // Helper function to extract the display value from a possibly nested value
  const extractValue = (fieldValue) => {
    if (fieldValue === null || fieldValue === undefined) return 0;
    if (typeof fieldValue === 'object') {
      const value = fieldValue.sortValue !== undefined 
        ? fieldValue.sortValue 
        : (fieldValue.value !== undefined ? fieldValue.value : 0);
      return parseFloat(value) || 0;
    }
    return parseFloat(fieldValue) || 0;
  };

  // Perform the aggregation
  data.forEach(row => {
    const rowValue = typeof row[rowField] === 'object' && row[rowField] !== null 
      ? (row[rowField].displayValue || row[rowField].value || 'N/A') 
      : (row[rowField] || 'N/A');
    
    const colValue = typeof row[columnField] === 'object' && row[columnField] !== null 
      ? (row[columnField].displayValue || row[columnField].value || 'N/A') 
      : (row[columnField] || 'N/A');
    
    const value = extractValue(row[valueField]);
    
    if (pivotMap[rowValue]) {
      // Perform different aggregations based on type
      switch (aggregationType) {
        case 'count':
          pivotMap[rowValue][colValue] = (pivotMap[rowValue][colValue] || 0) + 1;
          break;
        case 'avg': {
          // For average, we need to keep track of sum and count
          if (!pivotMap[rowValue][`${colValue}_sum`]) {
            pivotMap[rowValue][`${colValue}_sum`] = 0;
            pivotMap[rowValue][`${colValue}_count`] = 0;
          }
          pivotMap[rowValue][`${colValue}_sum`] += value;
          pivotMap[rowValue][`${colValue}_count`] += 1;
          // Calculate average
          pivotMap[rowValue][colValue] = pivotMap[rowValue][`${colValue}_sum`] / pivotMap[rowValue][`${colValue}_count`];
          break;
        }
        case 'min':
          if (pivotMap[rowValue][colValue] === 0 || value < pivotMap[rowValue][colValue]) {
            pivotMap[rowValue][colValue] = value;
          }
          break;
        case 'max':
          if (value > pivotMap[rowValue][colValue]) {
            pivotMap[rowValue][colValue] = value;
          }
          break;
        case 'sum':
        default:
          pivotMap[rowValue][colValue] = (pivotMap[rowValue][colValue] || 0) + value;
          break;
      }
    }
  });

  // Convert the map to an array of objects
  const pivotedData = Object.values(pivotMap);

  // Clean up any temporary tracking fields for avg calculation
  if (aggregationType === 'avg') {
    pivotedData.forEach(row => {
      uniqueColumnValues.forEach(colValue => {
        delete row[`${colValue}_sum`];
        delete row[`${colValue}_count`];
      });
    });
  }

  // Create a new schema for the pivoted data
  const pivotedSchema = {
    fields: [
      { name: rowField, type: schema.fields.find(f => f.name === rowField)?.type || 'STRING', mode: 'REQUIRED' },
      ...uniqueColumnValues.map(colValue => ({
        name: colValue,
        type: 'FLOAT', // Aggregated values are typically numeric
        mode: 'NULLABLE'
      }))
    ]
  };

  return { pivotedData, pivotedSchema };
};

/**
 * Creates a processed version of the data where nested object values are resolved
 * for more consistent display in the table view after transformation
 * @param {Array} data - The data to process
 * @returns {Array} - Processed data array
 */
export const processTransformedData = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  return data.map(row => {
    const processedRow = {};
    
    Object.entries(row).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        processedRow[key] = 'N/A';
      } else if (typeof value === 'object' && 'displayValue' in value) {
        processedRow[key] = {
          displayValue: value.displayValue,
          sortValue: value.sortValue !== undefined ? value.sortValue : value.displayValue
        };
      } else if (typeof value === 'object' && 'value' in value) {
        processedRow[key] = {
          displayValue: value.value,
          sortValue: typeof value.value === 'number' ? value.value : value.value
        };
      } else {
        processedRow[key] = value;
      }
    });
    
    return processedRow;
  });
};

/**
 * Revert to original data structure after transformation
 * @param {Object} query - The query object containing original data
 * @returns {Object} - Original query data
 */
export const revertToOriginal = (query) => {
  if (!query) return query;
  
  // If there's saved original data, use it
  if (query.originalResult && Array.isArray(query.originalResult)) {
    return {
      ...query,
      result: query.originalResult,
      schema: query.originalSchema,
      transformationType: null // Reset transformation type
    };
  }
  
  return query;
};
